import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useBreaches } from "../lib/alerts";
import { translate as t, tp, LANGS, BRANDS } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Modal, Button, Input } from "./ui";
import NotificationsBell from "./NotificationsBell";
import { AppFooter } from "./BrandFooter";
import { RecoverBanner } from "./LiveBackup";
import LeftScrollMirror from "./LeftScrollMirror";
import IdleGuard from "./IdleGuard";
import RepairBanner from "./RepairBanner";
import AdminConsole from "./AdminConsole";
import { Sun, Moon, LogOut, LayoutDashboard, KeyRound, TreePine, HeartHandshake, Coins, Boxes, Palette, Megaphone, Newspaper, Image as ImageIcon, Users, Bell, Activity as ActivityIcon, UserCog, RefreshCw, Settings as SettingsIcon, UserCircle, ShieldCheck, ShieldAlert, Headset, FlaskConical, Wallet, GripVertical, ArrowUpDown, Check, Eye, EyeOff, Network, Monitor, Maximize, Minimize, ZoomIn, ZoomOut, RotateCcw, PanelLeftClose, PanelLeftOpen, Ticket, MessageSquare, LineChart, Lock, LockOpen, ChevronDown, ChevronRight, CalendarClock, Languages, FileArchive } from "lucide-react";

const NAV = [
  { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/enabler", key: "master_enabler", icon: KeyRound },
  { to: "/app/runners", key: "runners", icon: UserCog, admin: true },
  { to: "/app/support", key: "support_console", icon: Headset, admin: true },
  { to: "/app/team", key: "team", icon: Network, admin: true },
  { to: "/app/my-team", key: "my_team", icon: Users, manager: true },
  { to: "/app/routing", key: "routing", icon: Headset, senior: true },
  { to: "/app/demo-codes", key: "demo_codes", icon: Ticket, adminOrSupport: true },
  { to: "/app/family", key: "family_tree", icon: TreePine },
  { to: "/app/benefits", key: "benefits", icon: HeartHandshake },
  { to: "/app/currency", key: "currency", icon: Coins },
  { to: "/app/holdings", key: "holdings", icon: Boxes },
  { to: "/app/recipients", key: "recipients", icon: Users, gate: (u) => u.includes("family_tree") && u.includes("benefits") },
  { to: "/app/alerts", key: "alerts", icon: Bell, badge: true },
  { to: "/app/activity", key: "activity", icon: ActivityIcon },
  { to: "/app/sync", key: "sync_backup", icon: RefreshCw },
  { to: "/app/privacy", key: "privacy", icon: ShieldCheck },
  { to: "/app/account", key: "account", icon: UserCircle },
  { to: "/app/purchases", key: "purchases", icon: Wallet },
  { to: "/app/audiences", key: "audiences", icon: Newspaper, admin: true },
  { to: "/app/dabl", key: "dabl", icon: Wallet, admin: true },
  { to: "/app/monthly-logs", key: "monthly_logs", icon: CalendarClock, admin: true },
  { to: "/app/security", key: "security", icon: ShieldAlert, admin: true },
  { to: "/app/reports-compliance", key: "reports_compliance", icon: FileArchive, admin: true },
  { to: "/app/cluster", key: "cluster", icon: Network, admin: true },
  { to: "/app/fop-seo", key: "fop_seo", icon: LineChart, admin: true },
  { to: "/app/listings", key: "store_listings", icon: Megaphone, admin: true },
  { to: "/app/reviews", key: "reviews", icon: MessageSquare, admin: true, reviewsBadge: true },
  { to: "/app/screenshots", key: "screenshots", icon: ImageIcon, admin: true },
  { to: "/app/settings", key: "settings", icon: SettingsIcon, admin: true },
];

// NUPAGERS — grouped navigation. Dashboard stays pinned above the groups.
const NUPAGERS = [
  { k: "N", name: "Nominees", keys: ["team", "my_team", "routing"] },
  { k: "U", name: "User / Personal", keys: ["recipients", "alerts", "activity", "sync_backup", "privacy", "account", "purchases"], defaultOpen: true },
  { k: "P", name: "People", keys: ["runners", "support_console", "demo_codes"] },
  { k: "A", name: "Admin", keys: ["settings", "security", "reports_compliance", "cluster"] },
  { k: "G", name: "Growth", keys: ["store_listings", "screenshots", "audiences"] },
  { k: "E", name: "Enabling", keys: ["dabl", "master_enabler", "monthly_logs"] },
  { k: "R", name: "Reserved Apps", keys: ["family_tree", "holdings", "currency", "benefits"], defaultOpen: true },
  { k: "S", name: "Statistics", keys: ["fop_seo", "reviews"] },
];

function RecoveryEmailGate() {
  const { user, refreshUser } = useApp();
  const [val, setVal] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.needs_recovery_email) return null;
  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await client.post("/auth/recovery-email", { recovery_email: val.trim() });
      await refreshUser();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Modal open onClose={() => {}} title="Add a recovery email" testid="recovery-email-gate">
      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-2"><ShieldCheck size={18} className="mt-0.5 text-[var(--primary)]" />
          <p className="opacity-70">For your security, please register a secondary recovery email. It's used to reset your password and to confirm any change to your email or phone.</p></div>
        <Input label="Recovery email" type="email" value={val} onChange={(e) => setVal(e.target.value)} data-testid="recovery-gate-input" />
        {err && <p className="text-[#DA1E28]" data-testid="recovery-gate-error">{err}</p>}
        <div className="flex justify-end"><Button onClick={save} disabled={busy || !val.trim()} data-testid="recovery-gate-save">Save &amp; continue</Button></div>
      </div>
    </Modal>
  );
}

function MultiScreenModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title="Split-screen & multi-screen views" testid="multiscreen-modal">
      <div className="space-y-3 text-sm">
        <p className="opacity-80">Demo, customer-support, live and Admin views can all run side by side. A browser shares its
          sign-in across tabs, so run <b>different accounts in separate windows or profiles</b>:</p>
        <ul className="list-disc space-y-1.5 ps-5 opacity-80">
          <li><b>Same account, two panes:</b> open a second window below and snap them side by side
            (<span className="rounded bg-black/10 px-1 dark:bg-white/10">Win + ◄/►</span> on Windows; tile via Mission Control on macOS).</li>
          <li><b>Different roles/demos at once:</b> open each in a separate <b>Incognito/Private window</b> or a distinct
            <b> browser profile</b> so their sessions stay independent, then tile across monitors.</li>
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => window.open(window.location.href, "_blank", "width=1100,height=800")} data-testid="ms-open-window">
            <Monitor size={15} className="me-1.5" /> Open this view in a new window
          </Button>
          <Button onClick={onClose} data-testid="ms-close">Got it</Button>
        </div>
      </div>
    </Modal>
  );
}

export default function Layout({ children }) {
  const { user, logout, theme, setTheme, bgColor, setBgColor, lang, setLang, chooseLang, unlocked, isAdmin, isSupport, delegatedKeys = [], isManager, isSenior, refreshUser } = useApp();
  const { breaches } = useBreaches();
  const nav = useNavigate();
  const doLogout = () => { logout(); nav(isSupport ? "/support-login" : user?.demo ? "/demo-login" : "/login"); };
  const [resetting, setResetting] = useState(false);
  const [msOpen, setMsOpen] = useState(false);
  const isDemo = !!user?.demo;

  // ---- Customer Service Profile nudge (once per sign-in / session) ----
  const pstatus = user?.profile_status;
  const [nudgeOpen, setNudgeOpen] = useState(false);
  useEffect(() => {
    if (isDemo) return;
    if (pstatus && pstatus.level !== "complete" && !sessionStorage.getItem("cs_profile_nudge")) {
      setNudgeOpen(true);
    }
  }, [pstatus?.level, isDemo]);
  const dismissNudge = () => { sessionStorage.setItem("cs_profile_nudge", "1"); setNudgeOpen(false); };
  const goCompleteProfile = () => { dismissNudge(); nav("/app/account"); };

  // ---- Pending-reviews badge (admin only) ----
  const [pendingReviews, setPendingReviews] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const fetchPending = () => client.get("/admin/reviews", { params: { status: "pending" } })
      .then((r) => { if (alive) setPendingReviews(r.data?.counts?.pending || 0); }).catch(() => {});
    fetchPending();
    const iv = setInterval(fetchPending, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin]);

  // ---- View controls: resize (zoom), sidebar collapse, fullscreen, revert (all roles) ----
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem("suite_zoom")) || 100);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("suite_sidebar_collapsed") === "1");
  const [isFs, setIsFs] = useState(false);
  useEffect(() => { localStorage.setItem("suite_zoom", String(zoom)); }, [zoom]);
  useEffect(() => { localStorage.setItem("suite_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };
  const changeZoom = (d) => setZoom((z) => Math.min(150, Math.max(70, z + d)));
  const revertView = () => { setZoom(100); setCollapsed(false); if (document.fullscreenElement) document.exitFullscreen?.(); };
  const atDefault = zoom === 100 && !collapsed && !isFs;

  // ---- Left-menu tab reordering (per-user, persisted) ----
  const visibleNav = NAV.filter((n) => (!n.gate || n.gate(unlocked)) && (!n.admin || isAdmin || delegatedKeys.includes(n.key)) && (!n.adminOrSupport || isAdmin || isSupport || delegatedKeys.includes(n.key)) && (!n.manager || isManager) && (!n.senior || isSenior));
  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [reorder, setReorder] = useState(false);
  const [dragKey, setDragKey] = useState(null);

  useEffect(() => { setOrder(Array.isArray(user?.nav_order) ? user.nav_order : []); }, [user?.nav_order]);
  useEffect(() => { setHidden(Array.isArray(user?.nav_hidden) ? user.nav_hidden : []); }, [user?.nav_hidden]);

  const orderedNav = [...visibleNav].sort((a, b) => {
    const ia = order.indexOf(a.key), ib = order.indexOf(b.key);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  const shownNav = reorder ? orderedNav : orderedNav.filter((n) => !hidden.includes(n.key));

  const persistOrder = (keys) => {
    setOrder(keys);
    client.put("/me/nav-order", { order: keys }).then(() => refreshUser && refreshUser()).catch(() => {});
  };
  const persistHidden = (keys) => {
    setHidden(keys);
    client.put("/me/nav-hidden", { hidden: keys }).then(() => refreshUser && refreshUser()).catch(() => {});
  };
  const toggleHidden = (key) => persistHidden(hidden.includes(key) ? hidden.filter((k) => k !== key) : [...hidden, key]);
  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const keys = orderedNav.map((n) => n.key);
    const from = keys.indexOf(dragKey), to = keys.indexOf(targetKey);
    if (from === -1 || to === -1) return;
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    persistOrder(keys);
    setDragKey(null);
  };
  const resetOrder = () => { persistOrder([]); persistHidden([]); };

  // ---- NUPAGERS group collapse / lock (per-browser) ----
  const [groups, setGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nupagers_groups") || "{}"); } catch { return {}; }
  });
  useEffect(() => { localStorage.setItem("nupagers_groups", JSON.stringify(groups)); }, [groups]);
  const groupOpen = (g) => {
    const s = groups[g.k] || {};
    if (s.lock === "open") return true;
    if (s.lock === "closed") return false;
    return s.open ?? !!g.defaultOpen;
  };
  const toggleGroup = (g) => setGroups((prev) => {
    const s = { ...(prev[g.k] || {}) };
    if (s.lock === "open" || s.lock === "closed") return prev; // locked — ignore header click
    s.open = !groupOpen(g);
    return { ...prev, [g.k]: s };
  });
  const cycleLock = (g) => setGroups((prev) => {
    const s = { ...(prev[g.k] || {}) };
    const cur = s.lock || "none";
    s.lock = cur === "none" ? "open" : cur === "open" ? "closed" : "none";
    if (s.lock === "open") s.open = true;
    if (s.lock === "closed") s.open = false;
    return { ...prev, [g.k]: s };
  });

  const resetSandbox = async () => {
    if (!window.confirm("Reset your sandbox to fresh demo data? Your current sandbox edits will be discarded.")) return;
    setResetting(true);
    try { await client.post("/support/sandbox/reset"); window.location.reload(); }
    catch { setResetting(false); }
  };

  const resetDemo = async () => {
    if (!window.confirm("Reset this demo to its fresh sample data? Any edits in this demo will be discarded.")) return;
    setResetting(true);
    try { await client.post("/demo/reset"); window.location.reload(); }
    catch { setResetting(false); }
  };

  const navByKey = Object.fromEntries(shownNav.map((n) => [n.key, n]));
  const navInner = (n) => {
    const brand = BRANDS[n.key];
    return (
      <>
        <n.icon size={18} className="shrink-0" />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate">{brand ? brand.name : t(n.key, lang)}</span>
          {brand && <span className="truncate text-[10px] font-normal opacity-55">{t(n.key, lang)}</span>}
        </span>
        {n.badge && breaches.length > 0 && (
          <span data-testid="alert-badge" className="ms-auto rounded-full bg-[#DA1E28] px-1.5 py-0.5 text-[10px] font-bold text-white">{breaches.length}</span>
        )}
        {n.reviewsBadge && pendingReviews > 0 && (
          <span data-testid="reviews-pending-badge" className="ms-auto rounded-full bg-[#DA1E28] px-1.5 py-0.5 text-[10px] font-bold text-white">{pendingReviews}</span>
        )}
      </>
    );
  };

  return (
    <div className="flex min-h-screen">
      <IdleGuard />
      <LeftScrollMirror left={collapsed ? "0px" : "16rem"} />
      {!collapsed && (
      <aside className="glass sticky top-0 z-40 hidden h-screen w-64 flex-col overflow-y-auto p-5 md:flex" data-testid="sidebar">
        <div className="mb-8 flex items-center gap-3" data-testid="brand-wordmark">
          <img src="/brand-logo.png" alt="Full Option Family Office" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="font-head text-sm font-bold tracking-tight" style={{ color: "var(--logo)" }}>FULL OPTION</div>
            <div className="font-head text-[11px] font-medium uppercase tracking-[0.18em] opacity-70">Family Office</div>
          </div>
          {isAdmin && <div className="ms-auto"><NotificationsBell /></div>}
        </div>
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider opacity-40">{tp("Menu", lang)}</span>
          <div className="flex items-center gap-2">
            {reorder && <button onClick={resetOrder} data-testid="nav-reorder-reset" className="text-[10px] opacity-60 hover:opacity-100">Reset</button>}
            <button onClick={() => setReorder((v) => !v)} data-testid="nav-reorder-toggle" title={reorder ? "Done reordering" : "Reorder tabs"}
              className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] lift ${reorder ? "bg-[var(--primary)] text-white" : "opacity-60 hover:opacity-100"}`}>
              {reorder ? <><Check size={12} /> {tp("Done", lang)}</> : <><ArrowUpDown size={12} /> {tp("Reorder", lang)}</>}
            </button>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1" data-testid="sidebar-nav">
          {reorder ? (
            <>
              {shownNav.map((n) => {
                const isHidden = hidden.includes(n.key);
                const brand = BRANDS[n.key];
                return (
                  <div key={n.to} draggable data-testid={`nav-reorder-${n.key}`}
                    onDragStart={() => setDragKey(n.key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(n.key)}
                    className={`flex items-center gap-3 rounded-md border border-dashed px-3 py-2 text-sm ${isHidden ? "opacity-40" : ""} ${dragKey === n.key ? "border-[var(--primary)] opacity-50" : "border-black/15 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/10"} cursor-grab active:cursor-grabbing`}>
                    <GripVertical size={14} className="opacity-40" />
                    <n.icon size={18} className="shrink-0" />
                    <span className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate">{brand ? brand.name : t(n.key, lang)}</span>
                      {brand && <span className="truncate text-[10px] font-normal opacity-55">{t(n.key, lang)}</span>}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); toggleHidden(n.key); }} data-testid={`nav-hide-${n.key}`}
                      title={isHidden ? "Show this tab" : "Hide this tab"} className="ms-auto opacity-60 hover:opacity-100">
                      {isHidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                );
              })}
              <p className="mt-2 px-1 text-[10px] leading-snug opacity-45" data-testid="nav-reorder-hint">{tp("Drag to reorder · tap the eye to hide/show a tab. Hidden tabs stay reachable by their link.", lang)}</p>
            </>
          ) : (
            <>
              {navByKey["dashboard"] && (
                <NavLink to={navByKey["dashboard"].to} end data-testid="nav-dashboard"
                  className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm lift ${isActive ? "bg-[var(--primary)] text-white" : "hover:bg-black/5 dark:hover:bg-white/10"}`}>
                  {navInner(navByKey["dashboard"])}
                </NavLink>
              )}
              <button onClick={doLogout} data-testid="logout-btn"
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-[#DA1E28] hover:bg-[#DA1E28]/10 lift">
                <LogOut size={18} className="shrink-0" /> {t("logout", lang)}
              </button>
              {NUPAGERS.map((g) => {
                let items = g.keys.map((k) => navByKey[k]).filter(Boolean);
                if (g.k === "N") {
                  const groupedKeys = new Set(NUPAGERS.flatMap((gg) => gg.keys).concat("dashboard"));
                  const extra = shownNav.filter((n) => !groupedKeys.has(n.key));
                  items = [...items, ...extra];
                }
                if (items.length === 0) return null;
                const open = groupOpen(g);
                const lock = (groups[g.k] || {}).lock || "none";
                const groupAlerts = items.reduce((sum, it) => sum + (it.badge ? breaches.length : 0) + (it.reviewsBadge ? pendingReviews : 0), 0);
                return (
                  <div key={g.k} className="mt-1.5" data-testid={`nav-group-${g.k}`}>
                    <div className="flex items-center gap-1 px-1">
                      <button onClick={() => toggleGroup(g)} data-testid={`nav-group-toggle-${g.k}`}
                        className="flex flex-1 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-75 hover:bg-black/5 dark:hover:bg-white/10">
                        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <span className="grid h-4 w-4 place-items-center rounded bg-black/10 text-[9px] font-bold dark:bg-white/15">{g.k}</span>
                        <span className="truncate">{tp(g.name, lang)}</span>
                        {groupAlerts > 0 && <span className="ms-auto rounded-full bg-[#DA1E28] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`nav-group-alerts-${g.k}`}>{groupAlerts}</span>}
                      </button>
                      <button onClick={() => cycleLock(g)} data-testid={`nav-group-lock-${g.k}`}
                        title={lock === "none" ? "Lock open" : lock === "open" ? "Locked open — click to lock closed" : "Locked closed — click to unlock"}
                        className={`rounded-md p-1 ${lock !== "none" ? "text-[var(--primary)]" : "opacity-45 hover:opacity-100"}`}>
                        {lock === "closed" ? <Lock size={13} /> : lock === "open" ? <LockOpen size={13} /> : <Lock size={13} />}
                      </button>
                    </div>
                    {open && (
                      <div className="mt-0.5 space-y-0.5 ps-2">
                        {items.length === 0 ? (
                          <p className="px-3 py-1.5 text-[10px] italic opacity-40" data-testid={`nav-group-empty-${g.k}`}>No items yet — new panels land here.</p>
                        ) : items.map((n) => (
                          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.key}`}
                            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm lift ${isActive ? "bg-[var(--primary)] text-white" : "hover:bg-black/5 dark:hover:bg-white/10"}`}>
                            {navInner(n)}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </nav>
        <div className="mt-4 space-y-3 border-t border-black/10 dark:border-white/10 pt-4 text-sm">
          <div className="flex items-center gap-2">
            <Palette size={16} className="opacity-60" />
            <input type="color" value={bgColor || (theme === "dark" ? "#0A0A0A" : "#F4F4F4")}
              onChange={(e) => setBgColor(e.target.value)} className="h-7 w-10 cursor-pointer rounded border-0 bg-transparent" data-testid="bg-color-picker" aria-label="Background color" title="Background color" />
            <select value={lang} onChange={(e) => chooseLang(e.target.value)} data-testid="language-select"
              className="ms-auto rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-xs">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.native}</option>)}
            </select>
          </div>
          <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-black/5 dark:hover:bg-white/10">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />} {theme === "dark" ? tp("Light mode", lang) : tp("Dark mode", lang)}
          </button>
          <button onClick={() => setMsOpen(true)} data-testid="multiscreen-btn"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-black/5 dark:hover:bg-white/10">
            <Monitor size={16} /> {tp("Split / multi-screen", lang)}
          </button>
          <div className="truncate text-xs opacity-60">{isSupport ? `@${user?.username} · sandbox` : user?.email}</div>
        </div>
      </aside>
      )}
      <main className="flex-1 overflow-x-hidden p-5 sm:p-8">
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="view-controls">
          <button onClick={() => setCollapsed((v) => !v)} data-testid="view-sidebar-toggle" title={collapsed ? "Show menu" : "Hide menu (wider view)"}
            className="flex items-center gap-1.5 rounded-md border border-black/15 px-2 py-1 text-xs lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
            {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />} {collapsed ? tp("Show menu", lang) : tp("Hide menu", lang)}
          </button>
          <div className="flex items-center overflow-hidden rounded-md border border-black/15 dark:border-white/15">
            <button onClick={() => changeZoom(-10)} disabled={zoom <= 70} data-testid="view-zoom-out" title="Zoom out"
              className="px-2 py-1 hover:bg-black/5 disabled:opacity-40 dark:hover:bg-white/10"><ZoomOut size={15} /></button>
            <span className="min-w-[3rem] px-1 text-center text-xs tabular-nums" data-testid="view-zoom-level">{zoom}%</span>
            <button onClick={() => changeZoom(10)} disabled={zoom >= 150} data-testid="view-zoom-in" title="Zoom in"
              className="px-2 py-1 hover:bg-black/5 disabled:opacity-40 dark:hover:bg-white/10"><ZoomIn size={15} /></button>
          </div>
          <button onClick={toggleFs} data-testid="view-fullscreen" title={isFs ? "Exit full screen" : "Full screen"}
            className="flex items-center gap-1.5 rounded-md border border-black/15 px-2 py-1 text-xs lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
            {isFs ? <Minimize size={15} /> : <Maximize size={15} />} {isFs ? tp("Exit full screen", lang) : tp("Full screen", lang)}
          </button>
          <button onClick={revertView} disabled={atDefault} data-testid="view-revert" title="Revert to default view"
            className="flex items-center gap-1.5 rounded-md border border-black/15 px-2 py-1 text-xs lift hover:bg-black/5 disabled:opacity-40 dark:border-white/15 dark:hover:bg-white/10">
            <RotateCcw size={15} /> {tp("Revert", lang)}
          </button>
          <label className="ms-auto flex items-center gap-1.5 rounded-md border border-black/15 px-2 py-1 text-xs lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10" title={tp("Change language", lang)} data-testid="shell-language-picker">
            <Languages size={15} className="shrink-0 opacity-70" />
            <select value={lang} onChange={(e) => chooseLang(e.target.value)} data-testid="shell-language-select"
              dir="ltr" className="cursor-pointer bg-transparent text-xs font-medium outline-none">
              {LANGS.map((l) => <option key={l.code} value={l.code} className="text-black">{l.native}</option>)}
            </select>
          </label>
        </div>
        <div style={{ zoom: zoom / 100 }} data-testid="view-zoom-wrapper">
        <AdminConsole />
        <Modal open={nudgeOpen} onClose={dismissNudge} title={tp("Complete your Customer Service Profile", lang)} testid="cs-profile-nudge">
          <div className="space-y-4 text-sm">
            <p>
              {pstatus?.level === "empty"
                ? tp("Your Customer Service Profile is empty.", lang)
                : pstatus?.level === "critical"
                ? tp("Your Customer Service Profile is critically incomplete.", lang)
                : tp("Your Customer Service Profile isn't finished yet.", lang)}
              {" "}{tp("It only takes a minute, and it's how our team confirms it's really you before helping with your account.", lang)}
            </p>
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
              <ShieldAlert size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <p><b>{tp("Important:", lang)}</b> {tp("Customer service cannot authenticate anyone on an empty or critically incomplete profile \u2014 which means we may be unable to recover your access or make changes on your behalf until it's complete.", lang)}</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={dismissNudge} data-testid="cs-nudge-later">{tp("Later", lang)}</Button>
              <Button onClick={goCompleteProfile} data-testid="cs-nudge-complete">{tp("Complete now", lang)}</Button>
            </div>
          </div>
        </Modal>
        {isDemo && (
          <div data-testid="demo-banner" className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-[#DA1E28]/50 bg-[#DA1E28]/10 px-4 py-2 text-sm">
            <FlaskConical size={16} className="text-[#DA1E28]" />
            <span><b className="tracking-wide text-[#DA1E28]">{tp("DEMO · SIMULATION.", lang)}</b> {tp("This is a sandboxed demonstration account with sample data only — nothing here is live or affects real accounts.", lang)}</span>
            <button data-testid="demo-reset-btn" onClick={resetDemo} disabled={resetting}
              className="ms-auto flex items-center gap-1.5 rounded-md border border-[#DA1E28]/40 px-3 py-1 text-xs font-medium text-[#DA1E28] lift disabled:opacity-60">
              <RefreshCw size={13} className={resetting ? "animate-spin" : ""} /> {resetting ? tp("Resetting…", lang) : tp("Reset demo data", lang)}
            </button>
          </div>
        )}
        {isSupport && (
          <div data-testid="sandbox-banner" className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-[#F1C21B]/50 bg-[#F1C21B]/15 px-4 py-2 text-sm">
            <FlaskConical size={16} className="text-[#8a6d00] dark:text-[#F1C21B]" />
            <span><b>Sandbox / training mode.</b> You are using demo data for support walkthroughs — nothing here affects real purchaser or Runner accounts.</span>
            <button data-testid="sandbox-reset-btn" onClick={resetSandbox} disabled={resetting}
              className="ms-auto flex items-center gap-1.5 rounded-md border border-[#8a6d00]/40 px-3 py-1 text-xs font-medium text-[#8a6d00] dark:text-[#F1C21B] lift disabled:opacity-60">
              <RefreshCw size={13} className={resetting ? "animate-spin" : ""} /> {resetting ? "Resetting…" : "Reset sandbox to fresh demo data"}
            </button>
          </div>
        )}
        <RepairBanner />
        <RecoverBanner />
        {children}
        <AppFooter />
        </div>
      </main>
      <RecoveryEmailGate />
      <MultiScreenModal open={msOpen} onClose={() => setMsOpen(false)} />
      {isDemo && (
        <div data-testid="demo-watermark" aria-hidden
          className="pointer-events-none fixed bottom-4 right-4 z-[100] select-none rounded-full border border-[#DA1E28]/40 bg-[#DA1E28]/90 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white shadow-lg">
          {tp("Demo · Simulation", lang)}
        </div>
      )}
    </div>
  );
}
